import { useState, useEffect, useCallback } from 'react';
import dayjs from 'dayjs';
import { useIncomes } from './useIncomes';
import { useIncomeActions } from './useIncomeActions';

// O backend pode devolver um objeto em erros de validação; renderizar objeto
// como filho quebra o React, então coagimos para texto antes de exibir.
const toErrorText = (data) =>
  typeof data === 'string' && data ? data : 'Não foi possível concluir a operação.';

export const incomeStatusConfig = {
  'A Receber': { color: 'warning', variant: 'outlined' },
  Recebida: { color: 'success', variant: 'filled' },
  Atrasada: { color: 'error', variant: 'filled' },
};

// Orquestra a tela de entradas. Consumido pelas versões web e mobile, que
// ficam responsáveis apenas pela apresentação.
export function useIncomesScreen() {
  const [selectedDate, setSelectedDate] = useState(dayjs());

  const {
    incomes,
    totalAmount,
    totalAmountToBeReceived,
    totalAmountReceived,
    isLoading: isListLoading,
    error: listError,
    refetch: getIncomes,
  } = useIncomes(selectedDate);
  const { remove, isLoading: isActionLoading } = useIncomeActions();

  const [openIncomeModal, setOpenIncomeModal] = useState(false);
  const [openReceiptModal, setOpenReceiptModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuSelectedIncome, setMenuSelectedIncome] = useState(null);
  const [selectedIncomeId, setSelectedIncomeId] = useState(null);

  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (listError) {
      setShowErrorMessage(true);
      setErrorMessage(toErrorText(listError));
    }
  }, [listError]);

  const goToPreviousMonth = useCallback(
    () =>
      setSelectedDate((previousDate) =>
        previousDate ? previousDate.subtract(1, 'month') : dayjs(),
      ),
    [],
  );

  const goToNextMonth = useCallback(
    () =>
      setSelectedDate((previousDate) => (previousDate ? previousDate.add(1, 'month') : dayjs())),
    [],
  );

  const saveSuccess = useCallback(
    (returnFromApi) => {
      setShowSuccessMessage(true);
      setSuccessMessage(toErrorText(returnFromApi.data));
      setOpenIncomeModal(false);
      setOpenReceiptModal(false);
      getIncomes();
    },
    [getIncomes],
  );

  const saveError = useCallback((returnFromApi) => {
    setShowErrorMessage(true);
    setErrorMessage(toErrorText(returnFromApi?.response?.data ?? returnFromApi));
  }, []);

  const openMenu = useCallback((event, income) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuSelectedIncome(income);
  }, []);

  const closeMenu = useCallback(() => {
    setMenuAnchorEl(null);
    setMenuSelectedIncome(null);
  }, []);

  const addIncome = useCallback(() => {
    closeMenu();
    setSelectedIncomeId(null);
    setOpenIncomeModal(true);
  }, [closeMenu]);

  const editIncome = useCallback(() => {
    setSelectedIncomeId(menuSelectedIncome.id);
    closeMenu();
    setOpenIncomeModal(true);
  }, [menuSelectedIncome, closeMenu]);

  const receive = useCallback(() => {
    setSelectedIncomeId(menuSelectedIncome.id);
    closeMenu();
    setOpenReceiptModal(true);
  }, [menuSelectedIncome, closeMenu]);

  // O menu fecha, mas a entrada selecionada precisa sobreviver até a confirmação
  const clickDelete = useCallback(() => {
    setMenuAnchorEl(null);
    setOpenDeleteModal(true);
  }, []);

  const closeDeleteModal = useCallback(() => {
    setOpenDeleteModal(false);
    setMenuSelectedIncome(null);
  }, []);

  const confirmDelete = useCallback(() => {
    if (!menuSelectedIncome) return;
    const incomeId = menuSelectedIncome.id;

    setOpenDeleteModal(false);
    setMenuSelectedIncome(null);
    remove(incomeId)
      .then((returnFromApi) => {
        setShowSuccessMessage(true);
        setSuccessMessage(toErrorText(returnFromApi.data));
        getIncomes();
      })
      .catch((returnFromApi) => {
        setShowErrorMessage(true);
        setErrorMessage(toErrorText(returnFromApi.response?.data));
        console.error('ops! ocorreu um erro' + returnFromApi);
      });
  }, [menuSelectedIncome, remove, getIncomes]);

  return {
    incomes,
    totalAmount,
    totalAmountToBeReceived,
    totalAmountReceived,
    isLoading: isListLoading || isActionLoading,

    selectedDate,
    setSelectedDate,
    goToPreviousMonth,
    goToNextMonth,

    menuAnchorEl,
    menuSelectedIncome,
    openMenu,
    closeMenu,

    openIncomeModal,
    setOpenIncomeModal,
    openReceiptModal,
    setOpenReceiptModal,
    selectedIncomeId,
    addIncome,
    editIncome,
    receive,

    openDeleteModal,
    clickDelete,
    closeDeleteModal,
    confirmDelete,

    saveSuccess,
    saveError,
    showSuccessMessage,
    setShowSuccessMessage,
    successMessage,
    showErrorMessage,
    setShowErrorMessage,
    errorMessage,
  };
}
